(() => {
/**
 * Free Shipping Progress Bar
 * Динамическое обновление прогресс-бара бесплатной доставки
 * Использует Dawn PubSub систему для обновления
 */

class FreeShippingProgress {
  constructor(debug = false) {
    this.threshold = 400000; // 4000 грн в копейках
    this.unsubscriber = null;
    this.debug = debug || window.Shopify?.designMode || false;
    this.lastCartState = null; // Кешируем последнюю комбинацию суммы и специальных тегов
    this.isUpdating = false; // Флаг для предотвращения множественных обновлений
    this.refreshPending = false;
    this.init();
  }

  log(...args) {
    // Убираем логи для production
    // console.log('[FreeShippingProgress]', ...args);
  }

  init() {
    // Получаем все прогресс-бары на странице
    this.progressBars = document.querySelectorAll('.free-shipping-progress');
    
    // Читаем threshold из data-атрибута если есть
    this.progressBars.forEach(bar => {
      const threshold = bar.dataset.freeShippingThreshold;
      if (threshold) {
        this.threshold = parseInt(threshold);
      }
    });

    // Подписываемся на события изменения корзины (Dawn PubSub)
    this.attachEventListeners();
    
    // Начальное состояние уже отрисовано Liquid вместе с актуальной корзиной.
  }

  attachEventListeners() {
    // Проверяем доступность функций PubSub
    if (typeof subscribe === 'undefined') {
      console.warn('PubSub not available, trying fallback method');
      this.attachFallbackListeners();
      return;
    }

    // Подписываемся на событие обновления корзины Dawn
    this.unsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const cartData = event?.cartData;

      if (cartData && typeof cartData.total_price === 'number' && Array.isArray(cartData.items)) {
        this.updateProgressBars(cartData);
      } else {
        this.onCartUpdateFallback();
      }
    });
    
    // Также слушаем событие загрузки секций (когда drawer открывается)
    document.addEventListener('shopify:section:load', (event) => {
      // Переинициализируем прогресс-бары если появились новые
      this.reinitProgressBars();
    });
    
    // Dawn заменяет содержимое корзины при обновлении секций.
    this.setupDOMObserver();
  }

  attachFallbackListeners() {
    // Fallback для случаев, когда PubSub недоступен
    // Наблюдаем за изменениями в DOM корзины
    this.setupDOMObserver();
  }

  setupDOMObserver() {
    let reinitTimeout = null;
    let updateTimeout = null;
    
    const observer = new MutationObserver((mutations) => {
      let shouldReinit = false;
      let hasCartChanges = false;
      
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            if (node.classList?.contains('free-shipping-progress') || 
                node.querySelector?.('.free-shipping-progress') ||
                node.classList?.contains('drawer__progress-wrapper') ||
                node.classList?.contains('cart__progress-wrapper')) {
              this.log('Progress bar added to DOM');
              shouldReinit = true;
            }
          }
        });
        
        hasCartChanges ||= mutation.target.closest?.('cart-items, cart-drawer') != null;
      });
      
      if (shouldReinit) {
        clearTimeout(reinitTimeout);
        reinitTimeout = setTimeout(() => this.reinitProgressBars(), 100);
      } else if (hasCartChanges) {
        clearTimeout(updateTimeout);
        updateTimeout = setTimeout(() => this.onCartUpdateFallback(), 150);
      }
    });

    document.querySelectorAll('cart-items, cart-drawer').forEach((container) => {
      observer.observe(container, { childList: true, subtree: true });
    });
    this.domObserver = observer;
  }

  async onCartUpdateFallback() {
    if (this.isUpdating) {
      this.refreshPending = true;
      return;
    }
    this.isUpdating = true;
    
    try {
      const cartUrl = `${window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`}.js`;
      const response = await fetch(cartUrl);
      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
      const cart = await response.json();
      this.updateProgressBars(cart);
    } catch (error) {
      console.error('Error updating free shipping progress:', error);
    } finally {
      this.isUpdating = false;

      if (this.refreshPending) {
        this.refreshPending = false;
        this.onCartUpdateFallback();
      }
    }
  }

  reinitProgressBars() {
    const newBars = document.querySelectorAll('.free-shipping-progress');
    
    if (newBars.length > 0) {
      this.progressBars = newBars;
      this.lastCartState = null;
      
      // Инициализируем прогресс-бары
      newBars.forEach((bar) => {
        const barFill = bar.querySelector('.free-shipping-progress__bar-fill');
        if (barFill) {
          const initialProgress = barFill.dataset.progress;
          if (initialProgress) {
            barFill.style.setProperty('width', `${initialProgress}%`, 'important');
            barFill.style.setProperty('background', 'rgb(var(--color-foreground))', 'important');
            barFill.style.setProperty('min-width', '1px', 'important');
            barFill.style.setProperty('display', 'block', 'important');
          }
        }
      });
      
    }
  }

  detectSpecialTags() {
    return {
      hasFreedel: document.querySelector('.cart-item[data-has-freedel="true"]') != null,
      hasPaiddel: document.querySelector('.cart-item[data-has-paiddel="true"]') != null,
    };
  }

  setPaiddelState(bar, hasPaiddel, hasFreedel) {
    bar.setAttribute('data-has-paiddel', hasPaiddel);
    bar.setAttribute('data-has-freedel', hasFreedel);

    if (hasPaiddel) {
      if (!bar.dataset.originalDisplay) {
        const inlineDisplay = bar.style.getPropertyValue('display');
        let originalDisplay = inlineDisplay;

        if (!originalDisplay) {
          const computedDisplay = window.getComputedStyle(bar).getPropertyValue('display');
          originalDisplay = computedDisplay && computedDisplay !== 'none' ? computedDisplay : 'block';
        }

        bar.dataset.originalDisplay = originalDisplay || 'block';
      }
      bar.setAttribute('hidden', 'true');
      bar.setAttribute('aria-hidden', 'true');
      bar.style.setProperty('display', 'none', 'important');
      bar.classList.add('free-shipping-progress--hidden');
    } else {
      bar.removeAttribute('hidden');
      bar.removeAttribute('aria-hidden');
      const originalDisplay = bar.dataset.originalDisplay || 'block';
      bar.style.setProperty('display', originalDisplay, 'important');
      bar.classList.remove('free-shipping-progress--hidden');
      delete bar.dataset.originalDisplay;
    }
  }

  cleanup() {
    if (this.unsubscriber) this.unsubscriber();
    if (this.domObserver) this.domObserver.disconnect();
  }

  async updateProgressBars(cart) {
    this.progressBars = document.querySelectorAll('.free-shipping-progress');

    if (this.progressBars.length === 0) {
      this.lastCartState = null;
      return;
    }

    const currentTotal = cart.total_price;
    const itemCount = cart.item_count ?? cart.items?.length ?? 0;

    let hasFreedel = false;
    let hasPaiddel = false;

    if (cart.items && cart.items.length > 0) {
      const result = this.detectSpecialTags();
      hasFreedel = result.hasFreedel;
      hasPaiddel = result.hasPaiddel;
    }

    const stateSignature = `${currentTotal}|${itemCount}|${hasFreedel}|${hasPaiddel}`;
    if (this.lastCartState === stateSignature) {
      return;
    }

    this.lastCartState = stateSignature;

    if (hasPaiddel) {
      this.progressBars.forEach((bar) => this.setPaiddelState(bar, true, hasFreedel));
      return;
    } else {
      this.progressBars.forEach((bar) => this.setPaiddelState(bar, false, hasFreedel));
    }
    
    // Если есть товар с freedel - доставка всегда бесплатная
    let remaining, progressPercent;
    if (hasFreedel) {
      remaining = 0;
      progressPercent = 100;
    } else {
      remaining = this.threshold - currentTotal;
      progressPercent = Math.min(100, Math.round((currentTotal / this.threshold) * 100));
    }

    this.progressBars.forEach(bar => {
      bar.classList.add('updating');
      setTimeout(async () => {
        await this.updateProgressBar(bar, currentTotal, remaining, progressPercent, hasFreedel, hasPaiddel);
        bar.classList.remove('updating');
      }, 50);
    });
  }

  updateProgressBar(bar, currentTotal, remaining, progressPercent, hasFreedel, hasPaiddel) {
    const messageElement = bar.querySelector('.free-shipping-progress__message');
    const textElement = bar.querySelector('.free-shipping-progress__text');
    const iconElement = bar.querySelector('.free-shipping-progress__icon');
    const barFill = bar.querySelector('.free-shipping-progress__bar-fill');
    const percentElement = bar.querySelector('.free-shipping-progress__percentage');
    
    if (remaining > 0) {
      messageElement?.classList.remove('free-shipping-progress__message--achieved');
      iconElement?.classList.remove('free-shipping-progress__icon--success');
      
      if (iconElement) {
        iconElement.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M14 18V6C14 5.44772 13.5523 5 13 5H2C1.44772 5 1 5.44772 1 6V18C1 18.5523 1.44772 19 2 19H3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 8H18L23 13V18C23 18.5523 22.5523 19 22 19H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="7" cy="19" r="2" stroke="currentColor" stroke-width="1.5"/><circle cx="17" cy="19" r="2" stroke="currentColor" stroke-width="1.5"/></svg>`;
      }
      
      if (textElement) {
        const msg = progressPercent >= 90 
          ? `✨ Ще трохи! Залишилось лише <strong class="free-shipping-progress__amount">${this.formatMoney(remaining)}</strong>`
          : progressPercent >= 75
          ? `🔥 Майже готово! До <strong>безкоштовної доставки</strong> залишилось <strong class="free-shipping-progress__amount">${this.formatMoney(remaining)}</strong>`
          : `До <strong>безкоштовної доставки</strong> залишилось <strong class="free-shipping-progress__amount">${this.formatMoney(remaining)}</strong>`;
        textElement.innerHTML = msg;
      }
    } else {
      messageElement?.classList.add('free-shipping-progress__message--achieved');
      iconElement?.classList.add('free-shipping-progress__icon--success');
      
      if (iconElement) {
        iconElement.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/></svg>`;
      }
      if (textElement) {
        textElement.innerHTML = `🎉 <strong>Вітаємо!</strong> Ви отримали безкоштовну доставку! (за умови повної оплати замовлення на відділення та поштомати)`;
      }
    }

    if (barFill) {
      barFill.style.setProperty('width', `${progressPercent}%`, 'important');
      barFill.style.setProperty('background', 'rgb(var(--color-foreground))', 'important');
      barFill.style.setProperty('min-width', '1px', 'important');
      barFill.style.setProperty('display', 'block', 'important');
      barFill.setAttribute('data-progress', progressPercent);
    }
    
    bar.setAttribute('data-has-freedel', hasFreedel);
    bar.setAttribute('data-has-paiddel', hasPaiddel);
    if (percentElement) {
      percentElement.textContent = `${progressPercent}%`;
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('uk-UA', {
      style: 'currency',
      currency: 'UAH',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  }
}

function initFreeShippingProgress() {
  if (window.freeShippingProgress) return;

  if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') {
    setTimeout(initFreeShippingProgress, 100);
    return;
  }
  
  window.freeShippingProgress = new FreeShippingProgress();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFreeShippingProgress);
} else {
  initFreeShippingProgress();
}

window.FreeShippingProgress = FreeShippingProgress;

window.addEventListener('beforeunload', () => {
  window.freeShippingProgress?.cleanup?.();
});
})();

